// src/components/layout/Footer.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Facebook, 
  Instagram, 
  Twitter,
  Heart,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { scrollToTop } from '../../utils/scrollUtils';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  
  const handleLinkClick = () => {
    scrollToTop();
  };
  
  return (
    <footer className="bg-primary-700 text-white">
      <div className="container-wide py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <Card className="bg-primary-600 border-primary-500 rounded-xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(42,93,60,0.5)] hover:border-secondary">
            <CardContent className="p-6">
              <Link to="/" onClick={handleLinkClick} className="flex flex-col items-center sm:items-start mb-6 group">
                <div className="bg-white/20 rounded-full p-2 mb-3">
                  <img 
                    src="/assets/images/oljarna-krajnc-fram-logo-transparent-80.png" 
                    alt="Oljarna Krajnc Fram" 
                    width={80}
                    height={80}
                  />
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-secondary transition-colors">
                  OLJARNA KRAJNC FRAM
                </h3>
              </Link>
              <p className="text-neutral mb-6">
                Tradicionalna pridelava bučnega olja že od leta 1927. Naša strast je ustvarjanje 
                kakovostnih izdelkov iz buč in bučnih semen s poudarkom na tradiciji in inovacijah.
              </p>
              <div className="flex space-x-4">
                <SocialLink href="https://www.facebook.com/oljarnakrajnc" icon={<Facebook size={20} />} label="Facebook" />
                <SocialLink href="https://instagram.com" icon={<Instagram size={20} />} label="Instagram" />
                <SocialLink href="https://twitter.com" icon={<Twitter size={20} />} label="Twitter" />
              </div>
            </CardContent>
          </Card>
          
          {/* Quick Links */}
          <Card className="bg-primary-600 border-primary-500 rounded-xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(42,93,60,0.5)] hover:border-secondary">
            <CardContent className="p-6">
              <h4 className="text-xl font-bold mb-6 text-white">Hitre povezave</h4>
              <ul className="space-y-3">
                <FooterLink to="/" label="Domov" onClick={handleLinkClick} />
                <FooterLink to="/o-nas" label="O nas" onClick={handleLinkClick} />
                <FooterLink to="/izdelki" label="Izdelki" onClick={handleLinkClick} />
                <FooterLink to="/kontakt" label="Kontakt" onClick={handleLinkClick} />
                <FooterLink to="#" label="Pogoji uporabe" onClick={handleLinkClick} />
                <FooterLink to="#" label="Politika zasebnosti" onClick={handleLinkClick} />
                <FooterLink to="#" label="Piškotki" onClick={handleLinkClick} />
              </ul>
            </CardContent>
          </Card>
          
          {/* Products */}
          <Card className="bg-primary-600 border-primary-500 rounded-xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(42,93,60,0.5)] hover:border-secondary">
            <CardContent className="p-6">
              <h4 className="text-xl font-bold mb-6 text-white">Izdelki</h4>
              <ul className="space-y-3">
                <FooterLink to="/izdelki#oils" label="Bučna olja" onClick={handleLinkClick} />
                <FooterLink to="/izdelki#spreads" label="Namazi" onClick={handleLinkClick} />
                <FooterLink to="/izdelki#seeds" label="Bučna semena" onClick={handleLinkClick} />
                <FooterLink to="/izdelki#specialties" label="Posebnosti" onClick={handleLinkClick} />
                <FooterLink to="#" label="Darilni seti" onClick={handleLinkClick} />
                <FooterLink to="#" label="Novi izdelki" onClick={handleLinkClick} />
              </ul>
            </CardContent>
          </Card>
          
          {/* Contact Info */}
          <Card className="bg-primary-600 border-primary-500 rounded-xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(42,93,60,0.5)] hover:border-secondary">
            <CardContent className="p-6">
              <h4 className="text-xl font-bold mb-6 text-white">Kontakt</h4>
              <ul className="space-y-4">
                <li className="flex">
                  <MapPin className="flex-shrink-0 mr-3 text-secondary" size={20} />
                  <div>
                    <span className="text-white">
                      Petra Krajnc s.p.<br />
                      Framska ulica 8<br />
                      2313 Fram
                    </span>
                    <div className="mt-1">
                      <a 
                        href="https://maps.google.com/?q=Framska+ulica+8,+2313+Fram,+Slovenia" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-xs text-secondary hover:text-white transition-colors inline-flex items-center"
                      >
                        Odpri v Google Maps
                        <ExternalLink size={12} className="ml-1" />
                      </a>
                    </div>
                  </div>
                </li>
                <li className="flex items-center">
                  <Phone className="flex-shrink-0 mr-3 text-secondary" size={20} />
                  <a href="tel:+38626089253" className="text-white hover:text-secondary transition-colors">
                    +386 (0)2 608 92 53
                  </a>
                </li>
                <li className="flex items-center">
                  <Phone className="flex-shrink-0 mr-3 text-secondary" size={20} />
                  <a href="tel:+38631654929" className="text-white hover:text-secondary transition-colors">
                    +386 31 654 929
                  </a>
                </li>
                <li className="flex items-center">
                  <Mail className="flex-shrink-0 mr-3 text-secondary" size={20} />
                  <a href="mailto:info@oljarna-krajnc.si" className="text-white hover:text-secondary transition-colors">
                    info@oljarna-krajnc.si
                  </a>
                </li>
                <li className="flex">
                  <Clock className="flex-shrink-0 mr-3 text-secondary" size={20} />
                  <div className="text-white">
                    <div>Pon - Pet: 8:00 - 16:00</div>
                    <div>Sob: 8:00 - 12:00</div>
                    <div>Ned: Zaprto</div>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
        
        {/* Bottom Bar */}
        <div className="mt-6 pt-4 border-t border-primary-600">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-neutral mb-4 md:mb-0">
              © {currentYear} Oljarna Krajnc Fram. Vse pravice pridržane.
            </p>
            
            <p className="mt-2 flex items-center justify-center md:justify-center gap-1 text-sm text-neutral">
              Made with <Heart size={16} className="text-red-500 animate-pulse" /> by <a href="https://digitalpro.dev/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-white transition-colors">DigitalPro</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Footer link component
const FooterLink: React.FC<{ 
  to: string; 
  label: string;
  onClick?: () => void;
}> = ({ to, label, onClick }) => {
  return (
    <li>
      <Link 
        to={to} 
        className="text-white hover:text-secondary transition-colors"
        onClick={onClick}
      >
        {label}
      </Link>
    </li>
  );
};

// Social media link component
const SocialLink: React.FC<{ 
  href: string; 
  icon: React.ReactNode; 
  label: string;
}> = ({ href, icon, label }) => {
  return (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="bg-primary-500 hover:bg-secondary transition-colors p-2 rounded-full text-white"
      aria-label={label}
    >
      {icon}
    </a>
  );
};

export default Footer;